import { Injectable } from '@angular/core';
import { Observable, map, combineLatest } from 'rxjs';

import { getDefaultWidgetConfigAdminForm } from '@feature/widget-admin/services/widget-admin-form';
import { WidgetConfiguration, WidgetConfigurationsService } from '@feature/widget-configs';
import { WidgetAdminHandler } from '@widget-admin/services/widget-admin-handler';
import { FormDefinition, FormFieldDefinition } from '@feature/_app_modules/dynamic-forms/dynamic-form/models/dynamic-form.interfaces';
import { mapWherePropertiesMatch } from '@common/index';

import { AdditionalInformationWidgetConfigurationHttpService } from
  '../services/additional-information-widget-configuration.http.service';
import { AdditionalInformationWidgetConfiguration } from '../models/additional-information-widget-configuration'
import { UdfGroup } from '../models/UdfGroups';
import { SectionControlItem } from '../additional-information.types';

@Injectable({
  providedIn: 'root'
})
export class AdditionalInformationWidgetAdminHandler implements WidgetAdminHandler {

  constructor(private readonly widgetConfigService: WidgetConfigurationsService,
    private readonly additionalInformationWidgetConfigurationHttpService: AdditionalInformationWidgetConfigurationHttpService) { }

  initialise(): boolean {
    return true;
  }


  getSectionObject(aiConfig: AdditionalInformationWidgetConfiguration, sections: UdfGroup[]): SectionControlItem[] {
    return sections.map((udfGroup: UdfGroup) => {
      const section = new SectionControlItem();
      section.Name = udfGroup.Name;
      section.Description = udfGroup.Description;
      section.Allowed = aiConfig.AllowedSections.some((x) => x.Name == udfGroup.Name);
      section.Default = aiConfig.DefaultSections.some((x) => x.Name == udfGroup.Name);
      return section;
    });
  }

  getAdminForm(selectedConfig: WidgetConfiguration): Observable<FormDefinition> {
    const formDefinition = getDefaultWidgetConfigAdminForm(selectedConfig);
    const additionalInformationConfig =
      this.additionalInformationWidgetConfigurationHttpService.getConfig(selectedConfig.id);
    const getSections = this.additionalInformationWidgetConfigurationHttpService.getSections(
      selectedConfig.widgetTypeId.replace('AdditionalInformation', ''));


    const additionalInfoAndSections = combineLatest([additionalInformationConfig, getSections]);
    return additionalInfoAndSections.pipe(
      map(([aiConfig, sections]) => {
        formDefinition.fields.push(
          {
            key: 'additionalInfoSelector',
            label: 'Allowed Sections',
            value: this.getSectionObject(aiConfig, sections),
            type: 'additional-info-selector',
            dataKey: 'Name',
            optionLabel: 'Description',
            options: sections,
            validation: [{ key: 'mustHaveAnAllowedAndDefaultSection', message: 'Must have at least one Allowed and Default section.' } ],
            asyncValidation: [],
          } as FormFieldDefinition
        );

        return formDefinition;
      }));
  }

  createCopyFromAdminForm(selectedConfig: WidgetConfiguration, adminFormValue: any): Observable<boolean> {
    const copyOfSelectedConfig: WidgetConfiguration = mapWherePropertiesMatch(selectedConfig, adminFormValue);
    const additionalInformationConfig: AdditionalInformationWidgetConfiguration =
      this.mapSelectedSections(adminFormValue);
    return this.additionalInformationWidgetConfigurationHttpService.createCopyOfConfiguration(copyOfSelectedConfig, additionalInformationConfig).pipe(
      map(() => true)
    );
  }

  mapSelectedSections(adminFormValue: any): AdditionalInformationWidgetConfiguration {
    let additionalInformationConfiguration = new AdditionalInformationWidgetConfiguration();
    const formValue: SectionControlItem[] = adminFormValue.additionalInfoSelector;
    additionalInformationConfiguration.AllowedSections = formValue.filter((section: SectionControlItem) => {
      return section.Allowed;
    }).map(((item: SectionControlItem) => {
      const resultItem = new UdfGroup();
      resultItem.Name = item.Name;
      return resultItem;
    }));

    additionalInformationConfiguration.DefaultSections = formValue.filter((section: SectionControlItem) => {
      return section.Default;
    }).map(((item: SectionControlItem) => {
      const resultItem = new UdfGroup();
      resultItem.Name = item.Name;
      return resultItem;
    }));

    return additionalInformationConfiguration;
  }

  saveConfigFromAdminForm(selectedConfig: WidgetConfiguration, adminFormValue: any): Observable<boolean> {
    const copyOfSelectedConfig: WidgetConfiguration = mapWherePropertiesMatch(selectedConfig, adminFormValue);
    const additionalInformationConfig: AdditionalInformationWidgetConfiguration =
      this.mapSelectedSections(adminFormValue);
    return this.additionalInformationWidgetConfigurationHttpService.saveAdminConfiguration(copyOfSelectedConfig, additionalInformationConfig).pipe(
      map(() => true)
    );
  }

  deleteConfigFromAdminForm(selectedConfig: WidgetConfiguration, adminFormValue: any): Observable<boolean> {
    const copyOfSelectedConfig: WidgetConfiguration = mapWherePropertiesMatch(selectedConfig, adminFormValue);
    return this.widgetConfigService.delete(copyOfSelectedConfig).pipe(
      map(() => true)
    );
  }
}
